import {
  AssignedProductLite,
  getExpiryTerm,
} from 'app/core/api/product-assignment/api-product-assignment.types';
import Icon from 'app/shared/components/icon/Icon';
import IconLib, {
  CalendarEmptyGraySvg,
  CalendarEmptySvg,
  // CalendarGraySvg,
} from 'app/shared/components/icon/icon-lib';
import styledConsts from 'app/styles/styledConsts';
import styledMixins from 'app/styles/styledMixins';
import {
  formatUnits,
  TimeAmount,
  wholeTimeUnitsRemaining,
} from 'app/tools/utils';
import { DateTime } from 'luxon';
import React from 'react';
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill';
import styled, { css } from 'styled-components/macro';
import { FixedLengthArray } from 'type-fest';

type SymbolWithTextProps = {
  className?: string;
  text: FixedLengthArray<string, 2>;
  symbol: React.ReactNode;
};
const SymbolWithTextBase = ({
  text,
  className,
  symbol,
}: SymbolWithTextProps) => (
  <div {...{ className }}>
    {symbol}
    {text.map(t => (
      <div key={t}>{t}</div>
    ))}
  </div>
);

const SymbolWithText = styled(SymbolWithTextBase)`
  flex: 0 0 33.3%;
`;

type IconInfoOpts = {
  $wide?: boolean;
  $size: number;
  $timeLeft: TimeAmount;
};
const WarrantyDaysRemaining = styled.div.attrs<IconInfoOpts>(p => ({
  children: Math.max(p.$timeLeft.duration, 0),
}))<IconInfoOpts>`
  ${styledMixins.flexCenter}
  text-align: center;
  line-height: 1;
  color: ${p =>
    p.$timeLeft.units === 'days'
      ? styledConsts.cError
      : styledConsts.cPrimary};
  ::after {
    content: '${p => formatUnits(p.$timeLeft)}';
    display: block;
    position: relative;
    font-size: 0.35em;
  }
`;

const DateText = styled.div`
  color: ${styledConsts.cPrimary};
  position: absolute;
  ${styledMixins.flexCenter}
  ${styledMixins.fullsize}
  top: 0;
  line-height: 1.2;
  text-align: center;
`;

const DateSymbolBox = styled.div`
  position: relative;
`;
const DateIconBox = styled.div`
  ${styledMixins.flexCenter}
`;
const DatePurchasedSymbol: React.VFC<{
  date: DateTime;
}> = ({ date }) => (
  <DateSymbolBox>
    <DateIconBox>
      {date.isValid ? <CalendarEmptySvg /> : <CalendarEmptyGraySvg />}
    </DateIconBox>

    <DateText>
      {date.isValid && (
        <>
          <div>{date.toFormat('dd/MM').toUpperCase()}</div>
          <div>{date.year}</div>
        </>
      )}
    </DateText>
  </DateSymbolBox>
);

const Container = styled.div<{ $iconSize: number }>`
  display: flex;
  align-items: center;
  justify-content: space-around;
  align-self: center;

  ${({ $iconSize: s }) => css`
    ${SymbolWithText} {
      > *:first-child {
        margin: 0 auto ${s * 0.08}px;
        width: ${s}px;
        height: ${s}px;
      }
      > *:not(:first-child) {
        font-size: ${s * 0.24}px;
        height: 1.5em;
        text-align: center;
      }
    }
    ${DateIconBox} {
      height: ${s}px;
    }
    ${DateText} {
      font-size: ${s * 0.22}px;
      > div:first-child {
        margin-top: ${s * 0.1}px;
      }
    }
    ${WarrantyDaysRemaining} {
      height: ${s}px;
      font-size: ${s * 0.65}px;
    }
  `}
`;

type Props = {
  assignedProduct?: AssignedProductLite;
  wide?: boolean;
  className?: string;
};
export default function IconBox({
  assignedProduct,
  wide,
  className,
}: Props): JSX.Element {
  const { height = 50, ref } = useResizeDetector();
  const maxSize = wide ? 44 : 34;
  const size = Math.min(height * 0.5, maxSize);

  const { purchaseDate: purchaseDateStr } = assignedProduct || {};
  const warrantyAssigned = !!assignedProduct?.warrantyDetails;

  const purchaseDate = DateTime.fromISO(purchaseDateStr || 'NaN');
  const expiryTerm = getExpiryTerm(assignedProduct);

  const $wide = wide;
  const warrantyTimeLeft = wholeTimeUnitsRemaining({
    startDate: purchaseDate,
    monthsDuration: expiryTerm,
  });

  const expirySymbolAndText = ((): Pick<
    SymbolWithTextProps,
    'text' | 'symbol'
  > => {
    if (!warrantyTimeLeft) {
      return {
        text:
          expiryTerm === 0 // Specified no warranty
            ? ['Expiry', 'N/A']
            : ['Expiry Info', 'Pending'],
        symbol: (
          <Icon icon={IconLib.INFO} color="#ccc" {...{ size }} />
        ),
      };
    } else if (warrantyTimeLeft.duration === -1) {
      return {
        text: ['Warranty', 'Expired'],
        symbol: (
          <Icon
            icon={IconLib.WARNING}
            color="#F6645B"
            {...{ size }}
          />
        ),
      };
    } else {
      return {
        text: ['Remaining', 'Warranty'],
        symbol: (
          <WarrantyDaysRemaining
            $size={size}
            $timeLeft={warrantyTimeLeft}
            {...{ $wide }}
          />
        ),
      };
    }
  })();

  return (
    <Container $iconSize={size} {...{ ref, className }}>
      <SymbolWithText
        symbol={
          <DatePurchasedSymbol date={purchaseDate} {...{ size }} />
        }
        {...{ $wide }}
        text={['Purchase', 'Date']}
      />
      <SymbolWithText
        symbol={
          <Icon
            // TODO: use a theme grey instead
            color={warrantyAssigned ? 'primary' : '#ccc'}
            icon={IconLib.PAGE}
            {...{ size }}
          />
        }
        {...{ $wide }}
        text={
          warrantyAssigned
            ? ['Warranty', 'Terms']
            : ['Terms', 'Pending']
        }
      />
      <SymbolWithText {...{ $wide }} {...expirySymbolAndText} />
    </Container>
  );
}
